/*
 * Copyright (c) Eric Traut
 * Acts as a simple language server protocol (LSP) client that exchanges
 * information with a language server.
 */

import { Range } from 'monaco-editor';
import { BrowserMessageReader, BrowserMessageWriter } from 'vscode-jsonrpc/browser';
import {
  CodeAction,
  CodeActionParams,
  CodeActionRequest,
  Command,
  CompletionItem,
  CompletionList,
  CompletionParams,
  CompletionRequest,
  CompletionResolveRequest,
  ConfigurationParams,
  createMessageConnection,
  Declaration,
  DeclarationParams,
  DeclarationRequest,
  Definition,
  DefinitionParams,
  Diagnostic,
  DiagnosticTag,
  DidChangeConfigurationParams,
  DidChangeTextDocumentParams,
  DidOpenTextDocumentParams,
  DocumentSymbol,
  DocumentSymbolParams,
  DocumentSymbolRequest,
  ExecuteCommandParams,
  ExecuteCommandRequest,
  Hover,
  HoverParams,
  HoverRequest,
  InitializeParams,
  InitializeRequest,
  Location,
  LocationLink,
  LogMessageParams,
  MarkupKind,
  MessageConnection,
  NotificationType,
  Position,
  PublishDiagnosticsParams,
  ReferenceParams,
  ReferencesRequest,
  RenameParams,
  RenameRequest,
  RequestType,
  SemanticTokens,
  SemanticTokensParams,
  SemanticTokensRequest,
  SignatureHelp,
  SignatureHelpParams,
  SignatureHelpRequest,
  SymbolInformation,
  TypeDefinitionParams,
  TypeDefinitionRequest,
  WorkspaceEdit,
  WorkspaceSymbol,
  WorkspaceSymbolParams,
  WorkspaceSymbolRequest,
} from 'vscode-languageserver-protocol';
import { LSP_DEBUG, lspDebugLog } from '../../../logging/lspDebug';
import { pyrightManifest } from '../../../workers/pyright-manifest';
import { DEFAULT_LSP_SESSION_OPTIONS, SessionOptions } from './LspSession';

// Resolve Pyright worker from the generated manifest (avoids hardcoding the hashed filename)
const pyrightWorkerModules = import.meta.glob<string>('../../../workers/pyright.*.worker.js', {
  query: '?worker',
  import: 'default',
  eager: true,
});
const pyrightWorkerKey = Object.keys(pyrightWorkerModules).find((k) => k.endsWith(pyrightManifest.workerFile));
if (!pyrightWorkerKey) {
  throw new Error(`Pyright worker not found for entry point: ${pyrightManifest.workerFile}`);
}
const WorkerURL = pyrightWorkerModules[pyrightWorkerKey] as unknown as new (options?: WorkerOptions) => Worker;

export interface LspDiagnosticPublish {
  diagnostics: Diagnostic[];
  documentVersion?: number;
  programCode: string;
}

export interface LspClientNotifications {
  onWaitingForInitialization?: (isWaiting: boolean) => void;
  onDiagnostics?: (publish: LspDiagnosticPublish) => void;
  onError?: (message: string) => void; // TODO
}

export interface DefinitionInfo {
  sourceCode: string;
  uri: string;
}

const rootPath = '/src/';

const rootUri = `file://${rootPath}`;

const fileName = 'Untitled.py';

const documentUri = rootUri + fileName;

const MAX_LSP_CODE_CACHE_ENTRIES = 50;

// The single-document URI Pyright knows the file as. The main Monaco model uses a
// different URI (e.g. inmemory://...), so callers remapping LSP edits onto the model
// must key off this, not `model.uri.toString()`.
export const LSP_DOCUMENT_URI = documentUri;

export class LspClient {
  public connection: MessageConnection | undefined;
  private _documentVersion = 1;
  private _documentText = '';
  private _serverDocumentText = '';
  private _pendingDocumentUpdate: Promise<void> = Promise.resolve();
  private _initializationPromise: Promise<void> | null = null;
  private _notifications: LspClientNotifications | null = null;
  private _versionProgramCode = new Map<number, string>();
  private _lastDiagnosticsByCode = new Map<string, { diagnostics: Diagnostic[]; documentVersion?: number }>();
  private _sessionOptions: SessionOptions = DEFAULT_LSP_SESSION_OPTIONS;

  requestNotification(notifications: LspClientNotifications) {
    this._notifications = notifications;
  }

  private rememberDocumentVersion(code: string, documentVersion: number): void {
    this._versionProgramCode.set(documentVersion, code);
    this.pruneCache(this._versionProgramCode);
  }

  private rememberDiagnostics(code: string, diagnostics: Diagnostic[], documentVersion?: number): void {
    this._lastDiagnosticsByCode.delete(code);
    this._lastDiagnosticsByCode.set(code, {
      diagnostics,
      documentVersion,
    });
    this.pruneCache(this._lastDiagnosticsByCode);
  }

  private pruneCache(cache: Map<unknown, unknown>): void {
    while (cache.size > MAX_LSP_CODE_CACHE_ENTRIES) {
      const oldestKey = cache.keys().next().value;
      cache.delete(oldestKey);
    }
  }

  private resolveProgramCodeForDiagnostics(documentVersion?: number): string | null {
    if (documentVersion !== undefined && documentVersion >= 0) {
      return this._versionProgramCode.get(documentVersion) ?? null;
    }
    return this._documentVersion === 1 ? this._serverDocumentText : null;
  }

  private emitDiagnosticsPublish(diagInfo: PublishDiagnosticsParams): void {
    const programCode = this.resolveProgramCodeForDiagnostics(diagInfo.version);
    if (programCode === null) return;

    const publish: LspDiagnosticPublish = {
      diagnostics: diagInfo.diagnostics,
      documentVersion: diagInfo.version,
      programCode,
    };

    this.rememberDiagnostics(programCode, diagInfo.diagnostics, diagInfo.version);
    this._notifications?.onDiagnostics?.(publish);
  }

  private replayCachedDiagnostics(code: string): void {
    const cached = this._lastDiagnosticsByCode.get(code);
    if (!cached) return;

    this._notifications?.onDiagnostics?.({
      diagnostics: cached.diagnostics,
      documentVersion: cached.documentVersion ?? this._documentVersion,
      programCode: code,
    });
  }

  public dispose() {
    try {
      this.connection?.dispose();
    } catch {
      // Ignore dispose errors
    } finally {
      this.connection = undefined;
      this._initializationPromise = null;
    }
  }

  private async ensureConnection(): Promise<MessageConnection> {
    await this.ensureInitialized();

    if (!this.connection) {
      throw new Error('Failed to initialize LSP connection');
    }
    return this.connection;
  }

  private async ensureInitialized(sessionOptions: SessionOptions = this._sessionOptions): Promise<void> {
    if (!this.connection) {
      this._initializationPromise ??= this.initialize(sessionOptions).finally(() => {
        this._initializationPromise = null;
      });
    }

    if (this._initializationPromise) {
      await this._initializationPromise;
    }
  }

  public updateCode = (code: string) => {
    this._documentText = code;
  };

  private async initialize(sessionOptions: SessionOptions = this._sessionOptions) {
    this._notifications?.onWaitingForInitialization?.(true);

    const foreground = new WorkerURL({ name: 'Pyright-foreground' });
    const workers: Worker[] = [foreground];

    foreground.postMessage({
      type: 'browser/boot',
      mode: 'foreground',
    });

    const connection = createMessageConnection(new BrowserMessageReader(foreground), new BrowserMessageWriter(foreground));

    connection.onDispose(() => {
      workers.forEach((w) => w.terminate());
    });

    let backgroundWorkerCount = 0;
    foreground.addEventListener('message', (e: MessageEvent) => {
      if (e.data && e.data.type === 'browser/newWorker') {
        // Create a new background worker.
        // The foreground worker has created a message channel and passed us
        // a port. We create the background worker and pass transfer the port
        // onward.
        const { initialData, port } = e.data;

        const background = new WorkerURL({ name: `Pyright-background-${++backgroundWorkerCount}` });
        workers.push(background);

        background.postMessage(
          {
            type: 'browser/boot',
            mode: 'background',
            initialData,
            port,
          },
          [port]
        );
      }
    });

    this.connection = connection;

    this.connection.listen();

    // Receive diagnostics before didOpen so the first publish cannot race registration.
    this.connection.onNotification(new NotificationType<PublishDiagnosticsParams>('textDocument/publishDiagnostics'), (diagInfo) => {
      const diagVersion = diagInfo.version ?? -1;

      if (LSP_DEBUG) lspDebugLog('Received diagnostics for version:', diagVersion);

      this.emitDiagnosticsPublish(diagInfo);
    });

    // Initialize the server.
    const init: InitializeParams = {
      rootUri,
      rootPath,
      processId: 1,
      capabilities: {
        // Advertise that this client will apply WorkspaceEdits returned by the server
        // (required for quick fixes / organize-imports to take effect).
        // See: https://microsoft.github.io/language-server-protocol/specifications/lsp/3.17/specification/#workspace_applyEdit
        workspace: {
          applyEdit: true,
          executeCommand: {
            dynamicRegistration: false,
          },
          workspaceEdit: {
            documentChanges: true,
          },
        },
        textDocument: {
          publishDiagnostics: {
            tagSupport: {
              valueSet: [DiagnosticTag.Unnecessary, DiagnosticTag.Deprecated],
            },
            versionSupport: true,
          },
          // Restrict the kinds of code actions the server may return to what this client handles.
          // See: https://microsoft.github.io/language-server-protocol/specifications/lsp/3.17/specification/#textDocument_codeAction
          codeAction: {
            dynamicRegistration: false,
            isPreferredSupport: true,
            codeActionLiteralSupport: {
              codeActionKind: {
                valueSet: ['quickfix', 'source', 'source.organizeImports'],
              },
            },
          },
          hover: {
            contentFormat: [MarkupKind.Markdown, MarkupKind.PlainText],
          },
          signatureHelp: {},
        },
      },
      initializationOptions: {
        files: {
          [rootPath + fileName]: this._documentText,
          [rootPath + 'pyrightconfig.json']: JSON.stringify({
            typeshedPath: '/typeshed',
            reportWildcardImportFromLibrary: false,
            ...sessionOptions,
          }),
        },
        traceCompletions: LSP_DEBUG, // Pyright server trace when LSP debug is enabled
      },
    };

    if (sessionOptions?.locale) {
      init.locale = sessionOptions.locale;
    }

    await this.connection.sendRequest(InitializeRequest.type, init);

    // Update the settings.
    await this.connection.sendNotification(new NotificationType<DidChangeConfigurationParams>('workspace/didChangeConfiguration'), {
      settings: {},
    });

    this.rememberDocumentVersion(this._documentText, this._documentVersion);

    // Simulate an "open file" event.
    await this.connection.sendNotification(new NotificationType<DidOpenTextDocumentParams>('textDocument/didOpen'), {
      textDocument: {
        uri: documentUri,
        languageId: 'python',
        version: this._documentVersion,
        text: this._documentText,
      },
    });
    this._serverDocumentText = this._documentText;

    // Log messages received by the language server for debugging purposes.
    this.connection.onNotification(new NotificationType<LogMessageParams>('window/logMessage'), (info) => {
      if (LSP_DEBUG) lspDebugLog('logMessage:', info.message);
    });

    // Handle requests for configurations.
    this.connection.onRequest(new RequestType<ConfigurationParams, unknown, unknown>('workspace/configuration'), (params) => {
      if (LSP_DEBUG) lspDebugLog('Language server config request:', params);
      return [];
    });
    this._notifications?.onWaitingForInitialization?.(false);
  }

  updateSettings = async (sessionOptions: SessionOptions) => {
    try {
      this._sessionOptions = sessionOptions;

      // If there is no active connection yet, initialize it once.
      if (!this.connection) {
        await this.ensureInitialized(sessionOptions);
        return;
      }

      await this.ensureInitialized(sessionOptions);

      // Otherwise, update configuration over the existing connection to avoid spawning new workers.
      await this.connection.sendNotification(new NotificationType<DidChangeConfigurationParams>('workspace/didChangeConfiguration'), {
        settings: {
          // Forward session options directly; pyright reads relevant keys dynamically
          reportWildcardImportFromLibrary: false,
          ...sessionOptions,
        },
      });
    } catch (error) {
      if (error instanceof Error) {
        // Access properties specific to Error object
        console.error('Error message:', error.message);
        console.error('Stack trace:', error.stack);
      } else if (typeof error === 'object' && error !== null && 'message' in error) {
        //Access the message property if it exists
        console.error('Error message:', error.message);
      } else {
        // Handle cases where the error is not an Error object
        console.error('Unknown error:', error);
      }
    }
  };

  /**
   * Ensures the Pyright server has `code` before a request (hover, completion, etc.) is issued.
   *
   * Dedupes against {@link _serverDocumentText} (what the server last acknowledged) rather than
   * {@link _documentText} (what the client has locally) so that a request which arrives after a
   * racing `updateTextDocument` but before its `didChange` has been sent still waits for the
   * server to catch up instead of querying stale state.
   *
   * Errors from the underlying `updateTextDocument` are swallowed: request methods (hover,
   * completion, etc.) uniformly return `null` on failure, so a flush rejection must not
   * escape through them. The error is already logged by `updateTextDocument`.
   */
  private async syncTextDocumentForRequest(code: string): Promise<void> {
    if (this._serverDocumentText === code) {
      return;
    }

    try {
      await this.updateTextDocument(code, { forceSnapshot: true });
    } catch {
      // Intentionally swallowed — callers fall through to the request, which has its own
      // .catch(() => null) path. Pyright will answer against whatever text it currently has.
    }
  }

  async getHoverInfo(code: string, position: Position): Promise<Hover | null> {
    await this.syncTextDocumentForRequest(code);

    const params: HoverParams = {
      textDocument: {
        uri: documentUri,
      },
      position,
    };

    const connection = await this.ensureConnection();
    const result = await connection.sendRequest(HoverRequest.type, params).catch(() => {
      // Don't return an error. Just return null (no info).
      return null;
    });

    if (LSP_DEBUG) lspDebugLog('getHoverInfo:', result);

    return result;
  }

  async getReferenceInfo(code: string, position: Position): Promise<Definition | LocationLink[] | null> {
    await this.syncTextDocumentForRequest(code);

    const params: ReferenceParams = {
      textDocument: {
        uri: documentUri,
      },
      position,
      context: {
        includeDeclaration: true,
      },
    };

    const connection = await this.ensureConnection();
    const result = await connection.sendRequest(ReferencesRequest.type, params).catch(() => {
      // Don't return an error. Just return null (no info).
      return null;
    });

    if (LSP_DEBUG) lspDebugLog('getReferenceInfo:', result);

    return result;
  }

  //    const type: ProtocolRequestType<DocumentSymbolParams, DocumentSymbol[] | SymbolInformation[] | null, DocumentSymbol[] | SymbolInformation[], void, DocumentSymbolRegistrationOptions>;

  async getDocumentSymbolInfo(): Promise<SymbolInformation[] | DocumentSymbol[] | null> {
    const params: DocumentSymbolParams = {
      textDocument: {
        uri: documentUri,
      },
    };

    const connection = await this.ensureConnection();
    const result = await connection.sendRequest(DocumentSymbolRequest.type, params).catch(() => {
      // Don't return an error. Just return null (no info).
      return null;
    });

    if (LSP_DEBUG) lspDebugLog('getDocumentSymbolInfo:', result);

    return result;
  }

  //    const type: ProtocolRequestType<SemanticTokensParams, SemanticTokens | null, SemanticTokensPartialResult, void, SemanticTokensRegistrationOptions>;

  async getSemanticTokensInfo(): Promise<SemanticTokens | null> {
    const params: SemanticTokensParams = {
      textDocument: {
        uri: documentUri,
      },
    };

    const connection = await this.ensureConnection();
    const result = await connection.sendRequest(SemanticTokensRequest.type, params).catch(() => {
      // Don't return an error. Just return null (no info).
      return null;
    });

    if (LSP_DEBUG) lspDebugLog('getSemanticTokensInfo:', result);

    return result;
  }

  async getWorkspaceSymbolInfo(queryString: string): Promise<SymbolInformation[] | WorkspaceSymbol[] | null> {
    const params: WorkspaceSymbolParams = {
      query: queryString,
    };

    const connection = await this.ensureConnection();
    const result = await connection.sendRequest(WorkspaceSymbolRequest.type, params).catch(() => {
      // Don't return an error. Just return null (no info).
      return null;
    });

    if (LSP_DEBUG) lspDebugLog('getWorkspaceSymbolInfo:', result);

    return result;
  }

  async getGotoDeclarationInfo(code: string, position: Position): Promise<Declaration | LocationLink[] | null> {
    await this.syncTextDocumentForRequest(code);

    const params: DeclarationParams = {
      textDocument: {
        uri: documentUri,
      },
      position,
    };

    const connection = await this.ensureConnection();
    const result = await connection.sendRequest(DeclarationRequest.type, params).catch(() => {
      // Don't return an error. Just return null (no info).
      return null;
    });

    if (LSP_DEBUG) lspDebugLog('getGotoDeclarationInfo:', result);

    return result;
  }

  async getGotoDefinitionInfo(code: string, position: Position, uri = documentUri): Promise<DefinitionInfo[] | null> {
    if (uri === documentUri) {
      await this.syncTextDocumentForRequest(code);
    }

    const params: DefinitionParams = {
      textDocument: {
        uri,
      },
      position,
    };

    const connection = await this.ensureConnection();
    const result = (await connection.sendRequest('sphero/gotoDefinition', params).catch(() => {
      // Don't return an error. Just return null (no info).
      return null;
    })) as DefinitionInfo[];

    if (LSP_DEBUG) lspDebugLog('getGotoDefinitionInfo:', result);

    return result;
  }

  async getGotoTypeDefinitionInfo(
    code: string,
    position: Position
  ): Promise<Definition | LocationLink[] | Location[] | [{ range: Range; uri: string }] | null> {
    await this.syncTextDocumentForRequest(code);

    const params: TypeDefinitionParams = {
      textDocument: {
        uri: documentUri,
      },
      position,
    };

    const connection = await this.ensureConnection();
    const result = await connection.sendRequest(TypeDefinitionRequest.type, params).catch(() => {
      // Don't return an error. Just return null (no info).
      return null;
    });

    if (LSP_DEBUG) lspDebugLog('getGotoTypeDefinitionInfo:', result);

    return result;
  }

  async getRenameEdits(code: string, position: Position, newName: string): Promise<WorkspaceEdit | null> {
    await this.syncTextDocumentForRequest(code);

    const params: RenameParams = {
      textDocument: {
        uri: documentUri,
      },
      position,
      newName,
    };

    const connection = await this.ensureConnection();
    const result = await connection.sendRequest(RenameRequest.type, params).catch(() => {
      // Don't return an error. Just return null (no edits).
      return null;
    });

    if (LSP_DEBUG) lspDebugLog('getRenameEdits:', result);

    return result;
  }

  async getCodeActions(code: string, range: Range, diagnostics: Diagnostic[] = [], only?: string[]): Promise<Array<CodeAction | Command> | null> {
    // Sync first: otherwise the server may compute actions against stale text.
    await this.syncTextDocumentForRequest(code);

    const params: CodeActionParams = {
      textDocument: {
        uri: documentUri,
      },
      range: {
        start: {
          line: range.startLineNumber - 1,
          character: range.startColumn - 1,
        },
        end: {
          line: range.endLineNumber - 1,
          character: range.endColumn - 1,
        },
      },
      context: {
        diagnostics,
        only,
      },
    };

    const connection = await this.ensureConnection();
    const result = await connection.sendRequest(CodeActionRequest.type, params).catch(() => {
      return null;
    });

    if (LSP_DEBUG) lspDebugLog('getCodeActions:', result);

    return result;
  }

  // LSP code actions come in two shapes: a CodeAction with an edit already attached, or a
  // Command the client must execute to obtain the edit. This resolves the second shape.
  async executeCommand(command: string, args: unknown[] = []): Promise<WorkspaceEdit | null> {
    const params: ExecuteCommandParams = {
      command,
      arguments: args,
    };

    const connection = await this.ensureConnection();
    const result = await connection.sendRequest(ExecuteCommandRequest.type, params).catch(() => {
      return null;
    });

    if (LSP_DEBUG) lspDebugLog('executeCommand:', result);

    return result as WorkspaceEdit | null;
  }

  async getSignatureHelp(code: string, position: Position): Promise<SignatureHelp | null> {
    await this.syncTextDocumentForRequest(code);

    const params: SignatureHelpParams = {
      textDocument: {
        uri: documentUri,
      },
      position,
    };

    const connection = await this.ensureConnection();
    const result = await connection.sendRequest(SignatureHelpRequest.type, params).catch(() => {
      // Don't return an error. Just return null (no info).
      return null;
    });

    if (LSP_DEBUG) lspDebugLog('getSignatureHelp:', result);

    return result;
  }

  async getCompletion(code: string, position: Position): Promise<CompletionList | CompletionItem[] | null> {
    await this.syncTextDocumentForRequest(code);

    const params: CompletionParams = {
      textDocument: {
        uri: documentUri,
      },
      position,
    };

    if (LSP_DEBUG) lspDebugLog('getCompletion params:', params);

    const connection = await this.ensureConnection();
    const result = await connection.sendRequest(CompletionRequest.type, params).catch(() => {
      // Don't return an error. Just return null (no info).
      return null;
    });

    if (LSP_DEBUG) lspDebugLog('getCompletion result:', result);

    return result;
  }

  async resolveCompletion(completionItem: CompletionItem): Promise<CompletionItem | null> {
    const connection = await this.ensureConnection();
    const result = await connection.sendRequest(CompletionResolveRequest.type, completionItem).catch(() => {
      // Don't return an error. Just return null (no info).
      return null;
    });

    if (LSP_DEBUG) lspDebugLog('resolveCompletion:', result);

    return result;
  }

  /**
   * Sends a new version of the text document to the language server.
   *
   * Updates are serialized through {@link _pendingDocumentUpdate} so that `didChange` notifications
   * use strictly monotonic document versions. Superseded queued updates are dropped when they reach
   * the head of the queue; only the latest local code needs to reach Pyright.
   *
   * The `code` closed over by each queued step is the value at call time; mutating
   * {@link _documentText} between queueing and execution does not change what is sent.
   *
   * Dedup: if, when this call reaches the head of the queue, {@link _serverDocumentText} already
   * equals `code`, no notification is sent and the current (non-bumped) `_documentVersion` is
   * returned. Callers must not assume the return value is monotonically increasing across calls.
   *
   * @returns the document version reflected on the server after this call completes.
   */
  async updateTextDocument(code: string, options: { forceSnapshot?: boolean } = {}): Promise<number> {
    this._documentText = code;
    const previousUpdate = this._pendingDocumentUpdate;
    const shouldForceSnapshot = options.forceSnapshot === true;

    const nextUpdate = previousUpdate
      .catch(() => {
        // Continue processing later updates even if an earlier send failed.
      })
      .then(async () => {
        const connection = await this.ensureConnection();

        if (!shouldForceSnapshot && this._documentText !== code) {
          return this._documentVersion;
        }

        if (this._serverDocumentText === code) {
          this.replayCachedDiagnostics(code);
          return this._documentVersion;
        }

        const documentVersion = ++this._documentVersion;
        if (LSP_DEBUG) lspDebugLog('updateTextDocument documentVersion:', documentVersion);
        this.rememberDocumentVersion(code, documentVersion);

        await connection.sendNotification(new NotificationType<DidChangeTextDocumentParams>('textDocument/didChange'), {
          textDocument: {
            uri: documentUri,
            version: documentVersion,
          },
          contentChanges: [
            {
              text: code,
            },
          ],
        });

        this._serverDocumentText = code;
        if (LSP_DEBUG) lspDebugLog('updateTextDocument success');
        return documentVersion;
      })
      .catch((err) => {
        console.error('LspClient.updateTextDocument error', err);
        throw err;
      });

    this._pendingDocumentUpdate = nextUpdate.then(
      () => undefined,
      () => undefined
    );

    return nextUpdate;
  }
}
